import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, useContent } from '@/stores/RootStore';
import { authApi } from '@/services/authApi';
import SubPageHero from '@/components/SubPageHero';
import ConfirmModal from '@/components/ui/ConfirmModal';
import s from './Inquiry.module.scss';

// [2026-09-12 사용자] 실시간 상담(Consult)과 동일한 실제 카카오톡 채널.
const KAKAO_URL = 'http://pf.kakao.com/_xikTUn/chat';

// 카테고리(백엔드와 동일 키) + flat SVG 아이콘.
const CATS = [
	{ key: 'PRODUCT', name: '제품·솔루션', icon: <><path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" /><path d="M12 12v9M4 7.5l8 4.5 8-4.5" /></> },
	{ key: 'INSTALL_AS', name: '설치·상담', icon: <><path d="M14.7 6.3a4 4 0 0 0-5.4 5.2l-5.6 5.6 2.2 2.2 5.6-5.6a4 4 0 0 0 5.2-5.4l-2.6 2.6-1.8-1.8 2.4-2.8z" /></> },
	{ key: 'QUOTE', name: '견적·도입', icon: <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6M8 13h8M8 17h6" /></> },
	{ key: 'DEMO', name: '체험·데모', icon: <><rect x="3" y="4" width="18" height="13" rx="1.5" /><path d="M8 21h8M12 17v4M11 9l3 2-3 2z" /></> },
	{ key: 'ETC', name: '기타', icon: <><circle cx="5" cy="12" r="1.3" /><circle cx="12" cy="12" r="1.3" /><circle cx="19" cy="12" r="1.3" /></> },
];

// [2026-09-11 사용자] 실무적 글자 제한 — 제목 100자·내용 2000자(백엔드 InquiryController와 동일 값).
const TITLE_MAX = 100;
const CONTENT_MAX = 2000;

/** [2026-09-10] 문의하기 — 통합 QnA 작성(로그인 필수). 카테고리 선택 + 제목·내용. 성공 시 내 문의로. */
function InquiryForm() {
	const auth = useAuth();
	const nav = useNavigate();
	const { company } = useContent();
	const telDigits = String(company.tel || '').split(/[,/]/)[0].replace(/[^0-9]/g, '');   // 대표번호 첫 항목
	// [2026-10-08 사용자/기획서 v7 block427] 제품 상세 등에서 '선택 제품명·페이지 URL'을 쿼리로 넘기면 문의폼에 자동입력.
	//   예: /support/inquiry?product=EVS-G200&from=/products/evs-g200 → 유형=제품, 제목·내용 프리필.
	const [sp] = useSearchParams();
	const pProduct = sp.get('product') || '';
	const pFrom = sp.get('from') || '';
	const [cat, setCat] = useState(pProduct ? 'PRODUCT' : '');
	const [title, setTitle] = useState(pProduct ? `${pProduct} 문의`.slice(0, TITLE_MAX) : '');
	const [content, setContent] = useState(() => {
		if (!pProduct && !pFrom) return '';
		const url = pFrom ? (pFrom.startsWith('http') ? pFrom : (typeof window !== 'undefined' ? window.location.origin : '') + pFrom) : '';
		return `${pProduct ? '관심 제품: ' + pProduct + '\n' : ''}${url ? '참고 페이지: ' + url + '\n' : ''}\n`;
	});
	const [file, setFile] = useState<File | null>(null);   // [2026-09-11] 첨부 1건(이미지/PDF, ≤5MB)
	const [fe, setFe] = useState<{ cat?: boolean; title?: boolean; content?: boolean }>({});
	const [err, setErr] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [done, setDone] = useState(false);   // [2026-09-12] 접수완료 = 네이티브 alert 대신 모달

	const ATTACH_MAX = 5 * 1024 * 1024;
	const ATTACH_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
	function onPickFile(f: File | null) {
		setErr(null);
		if (!f) { setFile(null); return; }
		if (!ATTACH_TYPES.includes(f.type)) { setErr('이미지(jpg·png) 또는 PDF만 첨부할 수 있습니다.'); return; }
		if (f.size > ATTACH_MAX) { setErr('첨부파일은 5MB 이하만 가능합니다.'); return; }
		setFile(f);
	}

	async function submit(e: React.FormEvent) {
		e.preventDefault();
		setErr(null);
		const fx = { cat: !cat, title: !title.trim(), content: !content.trim() };
		setFe(fx);
		if (fx.cat || fx.title || fx.content) return;
		setBusy(true);
		const fd = new FormData();
		fd.append('category', cat); fd.append('title', title.trim()); fd.append('content', content.trim());
		if (file) fd.append('file', file);
		const r = await authApi.inquiryCreate(fd);
		setBusy(false);
		if (!r.success) { setErr(r.message || '문의 접수에 실패했습니다.'); return; }
		setDone(true);   // [2026-09-12] 접수완료 = 네이티브 alert 대신 모달 → [확인]에서 내 문의로
	}

	return (
		<>
			<SubPageHero title="문의하기" subtitle="제품·견적·설치·체험 등 무엇이든 문의해주세요"
				image="/images/auth/hero.jpg" crumbs={[{ label: 'HOME' }, { label: '고객지원' }, { label: '문의하기' }]} />
			<ConfirmModal open={done} alertMode title="문의 접수 완료" confirmText="내 문의로"
				message="문의가 접수되었습니다. 답변은 마이페이지 > 내 문의에서 확인하실 수 있습니다."
				onConfirm={() => nav('/support/my-inquiries', { replace: true })} onCancel={() => {}} />
			<div className={s.wrap}>
				<div className={s.inner}>
					{/* [2026-09-12 사용자] 안내 배너(슬로건) — Autonics식. EVS 실제 연락 채널(전화·카카오톡) 노출로 신뢰·전환 강화. */}
					<div className={s.helpBanner}>
						<div className={s.hbMain}>
							<b>EVS 제품·솔루션에 대한 문의</b>를 담당자가 확인 후 정성껏 답변드립니다.
							<span className={s.hbSub}>빠른 상담이 필요하시면 전화·카카오톡 채널도 함께 이용해 주세요.</span>
						</div>
						<div className={s.hbChannels}>
							<a className={s.hbChip} href={`tel:${telDigits}`}>
								<svg viewBox="0 0 24 24" fill="none" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><path d="M5 4h4l2 5-3 2a11 11 0 0 0 5 5l2-3 5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" /></svg>
								전화 상담 <b>{company.tel}</b>
							</a>
							<a className={s.hbChip} href={KAKAO_URL} target="_blank" rel="noopener noreferrer">
								<svg viewBox="0 0 24 24" fill="none" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round"><path d="M12 4c-4.4 0-8 2.8-8 6.3 0 2.2 1.5 4.2 3.7 5.3l-.8 3 3.3-1.9c.6.1 1.2.2 1.8.2 4.4 0 8-2.8 8-6.3S16.4 4 12 4z" /></svg>
								카카오톡 채널 상담
							</a>
						</div>
					</div>
					{auth.ready && !auth.isAuthed ? (
						<div className={s.notice}>
							<p>문의는 로그인 후 이용하실 수 있습니다.<br />로그인하시면 문의 내역을 마이페이지에서 관리할 수 있습니다.</p>
							<button className={s.primary} style={{ maxWidth: 240, margin: '0 auto' }}
								onClick={() => nav('/login', { state: { from: '/support/inquiry' } })}>로그인하러 가기</button>
						</div>
					) : (
						<>
							<p className={s.sectionLabel}>CUSTOMER SUPPORT</p>
							<h2 className={s.h2}>어떤 문의인가요?</h2>
							<p className={s.lead}>유형을 선택하고 내용을 남겨주시면 담당자가 확인 후 답변드립니다.</p>
							{err && <div className={`${s.msg} ${s.error}`}>{err}</div>}

							<form onSubmit={submit}>
								<div className={s.cats}>
									{CATS.map((c) => (
										<button type="button" key={c.key}
											className={`${s.cat} ${cat === c.key ? s.on : ''}`}
											onClick={() => { setCat(c.key); setFe((f) => ({ ...f, cat: false })); }}>
											<svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">{c.icon}</svg>
											<span className={s.catName}>{c.name}</span>
										</button>
									))}
								</div>
								{fe.cat && <p className={s.errText} style={{ marginTop: -20, marginBottom: 20 }}>문의 유형을 선택해주세요.</p>}

								<div className={s.field}>
									<label className={s.label}>제목 <span className={s.count}>{title.length}/{TITLE_MAX}</span></label>
									<input className={`${s.input} ${fe.title ? s.err : ''}`} value={title}
										onChange={(e) => { setTitle(e.target.value.slice(0, TITLE_MAX)); setFe((f) => ({ ...f, title: false })); }}
										placeholder="문의 제목" maxLength={TITLE_MAX} />
									{fe.title && <p className={s.errText}>제목을 입력해주세요.</p>}
								</div>
								<div className={s.field}>
									<label className={s.label}>내용 <span className={s.count}>{content.length}/{CONTENT_MAX}</span></label>
									<textarea className={`${s.textarea} ${fe.content ? s.err : ''}`} value={content}
										onChange={(e) => { setContent(e.target.value.slice(0, CONTENT_MAX)); setFe((f) => ({ ...f, content: false })); }}
										placeholder="문의 내용을 자세히 적어주세요." maxLength={CONTENT_MAX} />
									{fe.content && <p className={s.errText}>내용을 입력해주세요.</p>}
								</div>

								<div className={s.field}>
									<label className={s.label}>첨부파일 <span className={s.count}>이미지·PDF · 5MB · 1개</span></label>
									{!file ? (
										<label className={s.fileBtn}>
											<input type="file" accept="image/jpeg,image/png,application/pdf" style={{ display: 'none' }}
												onChange={(e) => onPickFile(e.target.files?.[0] || null)} />
											파일 선택 (선택)
										</label>
									) : (
										<div className={s.fileChip}>
											<span className={s.fileName}>{file.name} · {(file.size / 1024 / 1024).toFixed(1)}MB</span>
											<button type="button" onClick={() => setFile(null)} aria-label="첨부 제거">✕</button>
										</div>
									)}
								</div>

								<div className={s.actions}>
									<button className={s.primary} disabled={busy}>{busy ? '접수 중…' : '문의 접수'}</button>
									<button type="button" className={s.ghost} onClick={() => nav('/support/my-inquiries')}>내 문의</button>
								</div>
							</form>
						</>
					)}
				</div>
			</div>
		</>
	);
}

export default observer(InquiryForm);
